import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Check,
  ShieldCheck,
  Lock,
  Award,
  Globe,
  Layers,
  GitBranch,
  Boxes,
  Crosshair,
} from "lucide-react";

const TRACK = [
  "Enumeration",
  "Exploitation",
  "Privilege escalation",
  "Reporting",
];

const CERT_CHIPS = ["SCyWF_v1", "NIST 800-181", "MITRE ATT&CK"];

const CREDENTIALS = [
  { icon: ShieldCheck, name: "OSCP", org: "Offensive Security" },
  { icon: Lock, name: "CEH", org: "EC-Council" },
  { icon: Award, name: "Security+", org: "CompTIA" },
  { icon: Globe, name: "CISSP", org: "ISC²" },
  { icon: Crosshair, name: "GPEN", org: "GIAC" },
];

const FRAMEWORKS = [
  { icon: Layers, name: "NIST 800-181", org: "Framework" },
  { icon: GitBranch, name: "MITRE ATT&CK", org: "Mapping" },
  { icon: Boxes, name: "SCyWF", org: "Workforce" },
  { icon: ShieldCheck, name: "OWASP Top 10", org: "Coverage" },
  { icon: Globe, name: "NICE", org: "Framework" },
];

function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          ob.disconnect();
        }
      },
      { threshold },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [threshold]);
  return [ref, seen];
}

/* counts 0 → end once `run` flips true */
function useCount(end, run, duration = 1500, delay = 0) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!run) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setV(end);
      return;
    }
    let raf;
    const t0 = performance.now() + delay;
    const step = (t) => {
      const p = Math.min(1, Math.max(0, (t - t0) / duration));
      setV(Math.round(end * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [run, end, duration, delay]);
  return v;
}

/* one track row — bar fills, % counts up, then the fill shimmers forever */
function TrackRow({ step, index, seen, last }) {
  const pct = useCount(100, seen, 1500, index * 150);
  return (
    <div className={last ? "" : "mb-3.5"}>
      <div className="mb-1.5 flex justify-between text-[12px]">
        <span className="text-(--color-text-secondary)">{step}</span>
        <span className="qv-mono text-(--color-primary)">{pct}%</span>
      </div>
      <span className="block h-1.5 overflow-hidden rounded-full bg-(--color-bg)">
        <span
          className="lp-cert-fill block h-full rounded-full bg-[linear-gradient(90deg,var(--color-primary),var(--color-primary-hover))] shadow-[0_0_10px_-2px_var(--color-primary)]"
          style={{
            width: seen ? "100%" : 0,
            transition: "width 1.5s cubic-bezier(.2,.8,.3,1)",
            transitionDelay: `${index * 150}ms`,
          }}
        />
      </span>
    </div>
  );
}

function Ticker({ items, reverse, speed = "40s" }) {
  /* The track translates by -50%, so the first half must already be wider than
     the viewport or a gap appears at the wrap. Repeat the set enough times to
     guarantee that on any screen, then duplicate the whole thing for the loop. */
  const half = [...items, ...items, ...items];
  const loop = [...half, ...half];
  return (
    <div
      className={`lp-cert-track flex w-max gap-3 pr-3 ${reverse ? "lp-cert-rev" : ""}`}
      style={{ "--lp-cert-speed": speed }}
    >
      {loop.map((item, i) => {
        const Icon = item.icon;
        return (
          <div
            key={`${item.name}-${i}`}
            className="flex items-center gap-2.5 whitespace-nowrap rounded-xl border border-(--color-border-solid) bg-(--color-bg-card) px-4 py-3 transition-colors duration-200 hover:border-(--color-primary)/50"
          >
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-(--color-border-solid) bg-(--color-primary-50) text-(--color-primary)">
              <Icon className="h-3.5 w-3.5" />
            </span>
            <span>
              <span className="block text-[13px] font-semibold text-(--color-text-primary)">
                {item.name}
              </span>
              <span className="block qv-mono text-[8.5px] uppercase tracking-wider text-(--color-text-muted)">
                {item.org}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function Certification() {
  const [ref, seen] = useInView();

  return (
    <section id="certification" className="relative py-20 lg:py-28">
      <div className="mx-auto max-w-350 px-5 md:px-10">
        {/* heading */}
        <div className="lp-reveal lp-down mx-auto max-w-2xl text-center">
          <div className="mb-4 flex items-center justify-center gap-2 qv-mono text-[11px] font-medium uppercase tracking-[0.24em] text-(--color-primary)">
            <span className="h-px w-6 bg-(--color-primary) opacity-50" />
            Certification
            <span className="h-px w-6 bg-(--color-primary) opacity-50" />
          </div>
          <h1
            className="lp-rise qv-glitch mt-6 text-[32px] sm:text-[40px] leading-tight font-normal tracking-tight text-(--color-text-primary)"
            style={{ "--lp-delay": "90ms" }}
          >
            {" "}
            Finish the track,{" "}
            <span className="lp-highlight">earn the credential</span>
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-(--color-text-secondary)">
            Coverage is scored as you go. Complete a track and Quantum issues a
            signed certificate with the frameworks it maps to.
          </p>
        </div>

        {/* progress → certificate */}
        <div
          ref={ref}
          className="lp-reveal mt-14 grid grid-cols-1 items-center gap-6 lg:grid-cols-[1fr_auto_1fr] lg:gap-8"
        >
          {/* track progress */}
          <div className="qv-card qv-noise relative overflow-hidden rounded-2xl p-6">
            {/* a light scans down the panel */}
            <span
              aria-hidden="true"
              className="lp-cert-scan pointer-events-none absolute left-0 right-0 h-16 bg-[linear-gradient(180deg,transparent,var(--color-primary-50),transparent)]"
            />
            <div className="mb-4 flex items-center justify-between">
              <span className="qv-mono text-[9.5px] uppercase tracking-wider text-(--color-text-muted)">
                Track progress · Offensive Operator
              </span>
              <span className="flex items-center gap-1.5">
                <span className="cyber-badge-dot" />
                <span className="qv-mono text-[9px] uppercase tracking-wider text-(--color-primary)">
                  complete
                </span>
              </span>
            </div>
            {TRACK.map((step, i) => (
              <TrackRow
                key={step}
                step={step}
                index={i}
                seen={seen}
                last={i === TRACK.length - 1}
              />
            ))}
          </div>

          {/* handoff — packets flow from the track into the certificate */}
          <div className="relative grid place-items-center text-(--color-primary)">
            <span
              aria-hidden="true"
              className="lp-cert-rail pointer-events-none absolute"
              style={{
                background:
                  "linear-gradient(90deg, transparent, color-mix(in srgb, var(--color-primary) 45%, transparent), transparent)",
              }}
            />
            <span
              aria-hidden="true"
              className="lp-cert-pkt pointer-events-none absolute h-1 w-1 rounded-full bg-(--color-primary) shadow-[0_0_8px_var(--color-primary)]"
            />
            <span
              aria-hidden="true"
              className="lp-cert-pkt pointer-events-none absolute h-1 w-1 rounded-full bg-(--color-primary) shadow-[0_0_8px_var(--color-primary)]"
              style={{ animationDelay: "1.1s" }}
            />
            <ArrowRight className="lp-cert-nudge relative z-1 h-6 w-6 rotate-90 lg:rotate-0" />
          </div>

          {/* the certificate */}
          <div
            className="lp-cert-breathe relative overflow-hidden rounded-2xl p-7 text-center"
            style={{
              border:
                "1px solid color-mix(in srgb, var(--color-primary) 45%, transparent)",
              background:
                "linear-gradient(160deg, color-mix(in srgb, var(--color-primary) 10%, transparent), transparent 46%), var(--color-bg-card)",
            }}
          >
            {/* hologram light catching the card */}
            <span
              aria-hidden="true"
              className="lp-cert-shine pointer-events-none absolute inset-y-0 w-1/2"
            />

            {/* live verification badge */}
            <span className="absolute right-4 top-4 z-1 inline-flex items-center gap-1.5 rounded-full border border-(--color-primary)/40 bg-(--color-primary-50) px-2 py-1 qv-mono text-[8px] uppercase tracking-wider text-(--color-primary)">
              <span className="cyber-badge-dot" />
              verified
            </span>
            {/* guilloche security pattern */}
            <svg
              aria-hidden="true"
              className="lp-cert-guilloche pointer-events-none absolute inset-0 h-full w-full opacity-[0.12]"
              viewBox="0 0 300 200"
              fill="none"
            >
              {[30, 46, 62, 78, 94, 110].map((r) => (
                <circle
                  key={r}
                  cx="150"
                  cy="100"
                  r={r}
                  stroke="var(--color-primary)"
                  strokeWidth="0.4"
                />
              ))}
            </svg>

            {/* seal */}
            <div className="relative mx-auto mb-3 h-16.5 w-16.5">
              <svg
                className="lp-cert-seal absolute inset-0 h-full w-full"
                viewBox="0 0 66 66"
                fill="none"
              >
                <circle
                  cx="33"
                  cy="33"
                  r="31"
                  stroke="var(--color-primary)"
                  strokeWidth="0.7"
                  strokeDasharray="3 4"
                />
              </svg>
              <span
                aria-hidden="true"
                className="lp-cert-ping absolute inset-2.75 rounded-full border border-(--color-primary)/50"
              />
              <div
                className="absolute inset-2.75 grid place-items-center rounded-full border border-(--color-primary) bg-(--color-primary-100) text-(--color-primary)"
                style={{
                  opacity: seen ? 1 : 0,
                  transform: seen ? "scale(1)" : "scale(.4)",
                  transition:
                    "opacity .5s ease .9s, transform .6s cubic-bezier(.2,1.5,.4,1) .9s",
                }}
              >
                <Check className="h-5.5 w-5.5" strokeWidth={2.6} />
              </div>
            </div>

            <h3 className="qv-serif text-xl font-medium text-(--color-text-primary)">
              Offensive Operator
            </h3>
            <div className="mt-1 qv-mono text-[9px] tracking-wider text-(--color-text-muted)">
              CERT-QV-2026-0714 · signed
            </div>

            <div className="mt-4 flex flex-wrap justify-center gap-1.5">
              {CERT_CHIPS.map((chip, i) => (
                <span
                  key={chip}
                  className="rounded-md border border-(--color-border-solid) bg-(--color-primary-50) px-2 py-1 qv-mono text-[8.5px] tracking-wider text-(--color-text-secondary)"
                  style={{
                    opacity: seen ? 1 : 0,
                    transition: "opacity .4s ease",
                    transitionDelay: `${1200 + i * 150}ms`,
                  }}
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* framework / credential ticker */}
        <div className="lp-reveal mt-14">
          <div className="mb-5 text-center qv-mono text-[9.5px] uppercase tracking-[0.2em] text-(--color-text-muted)">
            Scenarios map to the credentials your team trains toward
          </div>
          <div
            className="lp-cert-mask space-y-3"
            style={{
              WebkitMaskImage:
                "linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)",
              maskImage:
                "linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)",
            }}
          >
            <div className="overflow-hidden">
              <Ticker items={CREDENTIALS} speed="38s" />
            </div>
            <div className="overflow-hidden">
              <Ticker items={FRAMEWORKS} reverse speed="46s" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
